/**
 * Settlement Monitor Section
 * What has stopped settling, per league and cause (server: admin/settlementMonitor.ts).
 */

import { getLeagueLabel, parseLeagueKey } from '@parlay-picker/shared/league';
import type {
  PendingCause,
  SettlementLeagueCount,
  SettlementMonitorReport,
} from '@parlay-picker/shared/settlementMonitor';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import type { ReactElement } from 'react';

const CAUSE_LABELS: Record<PendingCause, string> = {
  missing_game_identity: 'No game date or team on the row',
  unknown_game_id: 'Game id is not one any provider uses',
  unresolved_team: 'Team name matches no registry team',
  lookup_unavailable: 'Provider failed the game lookup',
  provider_unauthorized: 'Provider plan does not cover it',
  game_not_found: 'Game not found yet',
  box_unavailable: 'Box score unreadable',
  player_lookup_unavailable: 'Missing player could not be looked up',
  missing_player: 'Player prop with no player',
  plays_unavailable: 'Play-by-play unreadable',
  side_unknown: 'Game line with no recorded side',
  ambiguous_player: 'Player name matches several players',
  stat_not_in_box: 'Stat missing from the box',
  box_incomplete: 'Box may still be arriving',
};

function leagueLabel(leagueKey: string): string {
  const key = parseLeagueKey(leagueKey);
  return key ? getLeagueLabel(key) : leagueKey;
}

function CheckRow({
  title,
  counts,
}: {
  title: string;
  counts: SettlementLeagueCount[];
}): ReactElement {
  const total = counts.reduce((sum, c) => sum + c.count, 0);
  return (
    <div className="bg-theme-bg rounded-lg p-4 space-y-2" data-testid="settlement-check">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-theme-text-tertiary">{title}</p>
        <p className={`text-xl font-bold ${total > 0 ? 'text-theme-warning' : 'text-theme-text'}`}>
          {total}
        </p>
      </div>
      {counts.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {counts.map((c) => (
            <span
              key={c.leagueKey}
              className="text-xs rounded px-2 py-0.5 bg-theme-surface text-theme-text-secondary"
            >
              {leagueLabel(c.leagueKey)} {c.count}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export function SettlementMonitorSection({
  report,
}: {
  report: SettlementMonitorReport | null;
}): ReactElement {
  if (!report) {
    return (
      <p className="text-sm text-theme-text-tertiary">
        Settlement monitor unavailable: its queries failed. Check the server logs.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 @3xl:grid-cols-2 gap-4">
        <CheckRow title="Predictions open 48h after start" counts={report.openPredictions} />
        <CheckRow title="Tracked legs open 48h after start" counts={report.openLegs} />
        <CheckRow
          title="Predictions closed with no outcome"
          counts={report.resolvedWithoutOutcome}
        />
        <CheckRow
          title="ML scores open past the void window"
          counts={report.mlScoresPastVoidWindow}
        />
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-medium text-theme-text-secondary">
          Why the last run left predictions open
          {report.lastRunAt && (
            <span className="text-theme-text-tertiary font-normal">
              {' '}
              ({new Date(report.lastRunAt).toLocaleTimeString()})
            </span>
          )}
        </h3>
        {!report.lastRunAt ? (
          <p className="text-sm text-theme-text-tertiary">
            No settlement run since the server started.
          </p>
        ) : report.pendingCauses.length === 0 ? (
          <p className="text-sm text-theme-success flex items-center gap-1">
            <CheckCircle2 className="w-4 h-4" /> Nothing left open
          </p>
        ) : (
          <ul className="space-y-1">
            {report.pendingCauses.map((c) => (
              <li
                key={`${c.leagueKey}-${c.cause}`}
                className="text-sm text-theme-text"
                data-testid="pending-cause"
              >
                <span className="font-medium">{leagueLabel(c.leagueKey)}</span> ·{' '}
                {CAUSE_LABELS[c.cause]}: {c.count}
                {c.examples.length > 0 && (
                  <span className="text-xs text-theme-text-tertiary">
                    {' '}
                    ({c.examples.join(', ')})
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {report.registryGaps.length > 0 && (
        <div className="space-y-1" data-testid="registry-gaps">
          <h3 className="text-sm font-medium text-theme-warning flex items-center gap-1">
            <AlertTriangle className="w-4 h-4" /> Teams the provider does not list (their games
            cannot settle)
          </h3>
          {report.registryGaps.map((g) => (
            <p key={g.leagueKey} className="text-xs text-theme-text-secondary">
              {leagueLabel(g.leagueKey)}: {g.teams.join(', ')}
            </p>
          ))}
        </div>
      )}

      {report.unauthorized.length > 0 && (
        <div className="space-y-1" data-testid="provider-unauthorized">
          <h3 className="text-sm font-medium text-theme-warning flex items-center gap-1">
            <AlertTriangle className="w-4 h-4" /> Provider plan refusing calls (rows wait, never
            void)
          </h3>
          {report.unauthorized.map((u) => (
            <p key={u.service} className="text-xs text-theme-text-secondary">
              {u.service}: {u.contexts.join(', ')}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
